/**
 * The pool drawer's picture (ADR 0088), as data: each account a lane in pool order, what state it
 * is in, which lane a launch lands on (in order) or how launches split (spread the load), and the
 * words a screen reader hears when a lane moves. PoolChannel draws it; nothing here touches the DOM.
 */
import { DEFAULT_LAUNCH_ACCOUNT_AGENT, headroomOf, launchAccountAgent, launchAccountSecretNeed } from '@orgus/protocol';
import type { AgentUsage, LaunchAccount, LaunchAccountsState, LaunchPoolStrategy, Ts } from '@orgus/protocol';
import { formatClock, formatPercent } from '@orgus/ui';
import { accountLimit, foldersOf } from './data.ts';

/** Where a lane stands: whether a launch can land on it now, and if not, why. */
export type LaneState = 'ready' | 'partial' | 'checking' | 'limited' | 'signed-out' | 'blocked';

/** The Prism facets a lane is tinted with, in pool order: the cool ones, so no lane reads as a warning. */
export const LANE_HUES = [1, 2, 5, 6] as const;
export type LaneHue = (typeof LANE_HUES)[number];

export interface PoolLane {
  id: string;
  name: string;
  account: Pick<LaunchAccount, 'kind' | 'builtIn'>;
  /** The dim line: its kind and what tells it apart. */
  meta: string;
  inPool: boolean;
  /** 1-based place in the pool; null outside it. */
  position: number | null;
  state: LaneState;
  /** The state in a few words: "Ready", "Over its limit until 4:45 AM". */
  label: string;
  /** Whether a launch may land on it now (checking counts: the pool tries it). */
  canRun: boolean;
  /** An active usage limit: when it began and when it lifts, for the clock ring. */
  limit: { at: Ts; until: Ts } | null;
  /** Percent left in its tightest usage window (best machine), null when no reading says. */
  headroom: number | null;
  hue: LaneHue;
}

/** The best headroom any machine reports for an account: its own readings, or the machine's own sign-in for the built-in account. */
export function headroomFor(usage: readonly AgentUsage[], a: Pick<LaunchAccount, 'id' | 'builtIn' | 'agent'>, now: Ts): number | null {
  const agent = a.agent ?? DEFAULT_LAUNCH_ACCOUNT_AGENT;
  let best: number | null = null;
  for (const u of usage) {
    if (u.agentKind !== agent) continue;
    if (a.builtIn ? !!u.accountId && u.accountId !== a.id : u.accountId !== a.id) continue;
    const h = headroomOf(u.windows, now);
    if (h !== null) best = best === null ? h : Math.max(best, h);
  }
  return best;
}

/** One account's lane state, as the pool sees it on any machine. */
export function laneStateOf(state: LaunchAccountsState, a: LaunchAccount, now: Ts): Pick<PoolLane, 'state' | 'label' | 'canRun' | 'limit'> {
  const limit = accountLimit(state, a.id);
  if (limit && limit.until > now) {
    if (limit.cause === 'auth') return { state: 'signed-out', label: 'Signed out', canRun: false, limit: { at: limit.at, until: limit.until } };
    return { state: 'limited', label: `Over its limit until ${formatClock(limit.until)}`, canRun: false, limit: { at: limit.at, until: limit.until } };
  }
  if (a.kind === 'subscription') {
    const folders = foldersOf(state, a);
    if (!folders.length) return a.builtIn ? { state: 'checking', label: 'Checking', canRun: true, limit: null } : { state: 'blocked', label: 'No folder yet', canRun: false, limit: null };
    const signed = folders.filter((f) => f.status?.state === 'signed-in').length;
    if (signed === folders.length) return { state: 'ready', label: 'Ready', canRun: true, limit: null };
    if (signed > 0) return { state: 'partial', label: `Ready on ${signed} of ${folders.length} machines`, canRun: true, limit: null };
    if (folders.every((f) => !f.status)) return { state: 'checking', label: 'Checking', canRun: true, limit: null };
    return { state: 'signed-out', label: 'Not signed in', canRun: false, limit: null };
  }
  if (launchAccountSecretNeed(a.kind) === 'required' && !a.secret) return { state: 'blocked', label: 'Key missing', canRun: false, limit: null };
  return { state: 'ready', label: 'Ready', canRun: true, limit: null };
}

/** Every account of the agent as a lane: the pool's members first, in pool order, then the rest in `others` order. */
export function poolLanes(
  state: LaunchAccountsState,
  accounts: readonly LaunchAccount[],
  members: readonly string[],
  opts: { now: Ts; usage?: readonly AgentUsage[]; meta: (a: LaunchAccount) => string; others?: readonly string[] },
): PoolLane[] {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const inPool = members.map((id) => byId.get(id)).filter((a): a is LaunchAccount => !!a);
  const rest = accounts.filter((a) => !members.includes(a.id));
  const order = opts.others ?? [];
  rest.sort((a, b) => rank(order, a.id) - rank(order, b.id));
  const lane = (a: LaunchAccount, i: number, pooled: boolean): PoolLane => ({
    id: a.id,
    name: a.name,
    account: { kind: a.kind, builtIn: a.builtIn },
    meta: opts.meta(a),
    inPool: pooled,
    position: pooled ? i + 1 : null,
    ...laneStateOf(state, a, opts.now),
    headroom: headroomFor(opts.usage ?? [], { id: a.id, builtIn: a.builtIn, agent: launchAccountAgent(a) }, opts.now),
    hue: LANE_HUES[i % LANE_HUES.length]!,
  });
  return [...inPool.map((a, i) => lane(a, i, true)), ...rest.map((a, i) => lane(a, inPool.length + i, false))];
}

const rank = (order: readonly string[], id: string) => {
  const i = order.indexOf(id);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
};

/** What the pool does with the next launch, for the picture and its one line of words. */
export interface PoolPlan {
  strategy: LaunchPoolStrategy;
  /** The lane the next launch lands on; null when none can run. */
  next: string | null;
  /** Each runnable lane's share of launches (sums to 1); in order, the next lane takes it all. */
  shares: Record<string, number>;
  /** Members passed over before the next lane (in order) or left out of the split (spread). */
  passed: string[];
  /** Whether any share comes from a real usage reading (else they are even). */
  measured: boolean;
  /** One line: "Lands on Claude Max", "Split by headroom: most to Claude Max". */
  line: string;
}

/**
 * The next launch, as choosePoolAccount picks it: in order, the first member that can run; spread,
 * the one with the most headroom (unknown counts as full), then the one launched least recently.
 * Spread's shares follow the headroom, so over many launches the load goes where room is left.
 */
export function poolPlan(lanes: readonly PoolLane[], strategy: LaunchPoolStrategy, launchedAt: Record<string, Ts> = {}): PoolPlan {
  const members = lanes.filter((l) => l.inPool);
  const open = members.filter((l) => l.canRun);
  if (!members.length) return { strategy, next: null, shares: {}, passed: [], measured: false, line: 'Add an account to the pool' };
  if (!open.length) return { strategy, next: null, shares: {}, passed: members.map((l) => l.id), measured: false, line: 'No account can run now: launches wait' };
  if (strategy === 'in-order') {
    const next = open[0]!;
    const passed = members.slice(0, members.indexOf(next)).map((l) => l.id);
    return { strategy, next: next.id, shares: { [next.id]: 1 }, passed, measured: false, line: `Lands on ${next.name}` };
  }
  const weight = (l: PoolLane) => Math.max(1, l.headroom ?? 100);
  const total = open.reduce((s, l) => s + weight(l), 0);
  const shares = Object.fromEntries(open.map((l) => [l.id, weight(l) / total]));
  const next = [...open].sort((a, b) => (b.headroom ?? 100) - (a.headroom ?? 100) || (launchedAt[a.id] ?? 0) - (launchedAt[b.id] ?? 0))[0]!;
  const measured = open.some((l) => l.headroom !== null);
  const passed = members.filter((l) => !l.canRun).map((l) => l.id);
  const line = open.length === 1 ? `Lands on ${next.name}, the only one that can run` : measured ? `Split by headroom: most to ${next.name}` : `Split evenly across ${open.length} accounts`;
  return { strategy, next: next.id, shares, passed, measured, line };
}

/** A share as the lane shows it: "76%". */
export const shareLabel = (share: number) => formatPercent(share);

/**
 * How far a limit has run toward its reset, 0 at the hit to 1 at the reset: the clock ring's fill.
 * A limit with no start before `now` reads as just begun.
 */
export function limitProgress(limit: { at: Ts; until: Ts }, now: Ts): number {
  const span = limit.until - limit.at;
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (now - limit.at) / span));
}

// ── Reordering ───────────────────────────────────────────────────────────────

/** The divider between the pool and the accounts outside it, as an item of the one sortable list. */
export const POOL_DIVIDER = '__pool-divider__';

/** The sortable list: members, the divider, then the rest. */
export const sortableOrder = (lanes: readonly PoolLane[]): string[] => [...lanes.filter((l) => l.inPool).map((l) => l.id), POOL_DIVIDER, ...lanes.filter((l) => !l.inPool).map((l) => l.id)];

/** Split a sorted list back into the pool's members and the order of the rest. */
export function splitOrder(order: readonly string[]): { members: string[]; others: string[] } {
  const at = order.indexOf(POOL_DIVIDER);
  if (at < 0) return { members: [...order], others: [] };
  return { members: order.slice(0, at), others: order.slice(at + 1) };
}

/** Move one item a step up or down the sorted list; crossing the divider joins or leaves the pool. */
export function stepOrder(order: readonly string[], id: string, by: -1 | 1): string[] {
  const i = order.indexOf(id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= order.length) return [...order];
  const next = [...order];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

/** Add an account to the end of the pool, or take it out to the top of the rest. */
export function toggleOrder(order: readonly string[], id: string, on: boolean): string[] {
  const rest = order.filter((x) => x !== id);
  const at = rest.indexOf(POOL_DIVIDER);
  if (at < 0) return on ? [id, ...rest] : [...rest, id];
  return on ? [...rest.slice(0, at), id, ...rest.slice(at)] : [...rest.slice(0, at + 1), id, ...rest.slice(at + 1)];
}

/** What a screen reader hears after a move: "Claude Max moved to 2 of 3 in the pool". */
export function moveAnnouncement(before: readonly string[], after: readonly string[], id: string, name: string): string | null {
  const was = splitOrder(before).members;
  const now = splitOrder(after).members;
  const i = was.indexOf(id);
  const j = now.indexOf(id);
  if (i < 0 && j < 0) return null;
  if (i < 0) return `${name} joined the pool at ${j + 1} of ${now.length}`;
  if (j < 0) return `${name} left the pool`;
  if (i === j) return null;
  return `${name} moved to ${j + 1} of ${now.length} in the pool`;
}
